import React from "react";
import { ChevronRight, Sparkles } from "lucide-react";

import { Tag } from "@/components/kit";
import { useApp } from "@/store";

export function ForYouBadge({ testId }) {
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1 self-start rounded-full bg-sky px-2 py-1 font-body-bold text-[10px] text-navy">
      <Sparkles className="h-2.5 w-2.5" aria-hidden /> Recommended for your hair
    </span>
  );
}

function ImgCard({ style, onClick, testId, forYou, meta, className }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} className={`focus-ring group relative block overflow-hidden rounded-[20px] bg-tert text-left transition-transform duration-200 active:scale-[0.98] ${className}`}>
      <img src={style.image_url} alt={style.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="scrim absolute inset-x-0 bottom-0 h-[65%]" />
      {forYou ? <div className="absolute left-2 right-2 top-2"><ForYouBadge testId={`${testId}-for-you`} /></div> : null}
      <div className="absolute inset-x-3 bottom-3">
        <p className="line-clamp-2 font-display-bold text-[17px] leading-5 text-white">{style.name}</p>
        <p className="mt-0.5 font-body-medium text-[11px] capitalize text-soft">{meta}</p>
      </div>
    </button>
  );
}

export function StyleGridCard({ style, onClick, testId, forYou }) {
  return <ImgCard style={style} onClick={onClick} testId={testId} forYou={forYou} meta={`${style.styling_time} min · ${style.difficulty}`} className="aspect-[3/4] w-full" />;
}

export function StyleMiniCard({ style, onClick, testId, forYou, width = 150 }) {
  return (
    <div style={{ width }} className="shrink-0">
      <ImgCard style={style} onClick={onClick} testId={testId} forYou={forYou} meta={`${style.styling_time} min · ${style.finish}`} className="h-[200px] w-full" />
    </div>
  );
}

export function StyleRowCard({ style, explanation, rank, faceNote, onClick, testId, forYou }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} className="focus-ring flex w-full items-center gap-3 rounded-[20px] border border-line bg-white p-2 pr-3 text-left transition-all hover:border-ocean hover:shadow-card active:scale-[0.99]">
      <div className="relative h-28 w-[92px] shrink-0 overflow-hidden rounded-xl bg-tert">
        <img src={style.image_url} alt={style.name} loading="lazy" className="h-full w-full object-cover" />
        {rank ? <span className="absolute left-1.5 top-1.5 flex h-[26px] w-[26px] items-center justify-center rounded-full bg-navy font-display-bold text-[13px] text-white">{rank}</span> : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {forYou ? <ForYouBadge testId={`${testId}-for-you`} /> : null}
        <p className="t-title truncate">{style.name}</p>
        <p className={`t-caption line-clamp-3 ${explanation ? "!text-ink" : ""}`}>{explanation || style.description}</p>
        {faceNote ? <p className="t-caption truncate !text-ocean">{faceNote}</p> : null}
        <div className="mt-0.5 flex gap-1.5">
          <Tag label={`${style.styling_time} min`} />
          <Tag label={style.finish} />
        </div>
      </div>
      <ChevronRight className="h-[18px] w-[18px] shrink-0 text-mute" aria-hidden />
    </button>
  );
}

/** Neutral branded placeholder — never depicts packaging. */
export function ProductPlaceholder({ name, height = 140 }) {
  const short = (name || "").replace(/^BREEZ\s*/i, "");
  return (
    <div className="flex flex-col justify-end overflow-hidden rounded-xl bg-gradient-to-br from-sky to-off p-3" style={{ height }}>
      <span className={`truncate font-display text-navy ${height < 130 ? "text-base tracking-[2px]" : "text-[22px] tracking-[4px]"}`}>BREEZ</span>
      <span className="mt-0.5 line-clamp-2 font-body-semi text-[13px] text-navy">{short}</span>
      <span className="mt-1 font-body text-[10px] text-mute">Image coming soon</span>
    </div>
  );
}

/** Official logo when an admin uploaded one, otherwise the text wordmark. */
export function BrandMark({ height, className, testId }) {
  const { brandLogo } = useApp();
  if (brandLogo) return <img data-testid={testId ? `${testId}-logo` : "brand-logo"} src={brandLogo} alt="BREEZ" style={{ height, width: height * 4 }} className="object-contain object-left" crossOrigin="anonymous" />;
  return <span data-testid={testId} className={className}>BREEZ</span>;
}
